import type { SchemaContext } from "../common";
import type { FieldValue, FieldValues } from "../../Schema";

export * from "./external-sources/Cluster";


/**
 * Context for the cluster schema. Same shared shape as `SchemaContext`; cluster
 * has no SOP field so it carries no `sops` (unlike `EquipmentContext`).
 */
export type ClusterContext = SchemaContext;

/**
 * Shared types and helpers for the cluster-component fields (subEquipments,
 * leadingSoftwareVersionId, trackPositionInCluster). The cluster loads these
 * from the GraphQL `subEquipment` connection.
 */

/** A single sub-equipment row within the GraphQL `subEquipment` connection. */
export interface SubEquipmentItem {
  id?: string;
  positionInCluster?: number;
  isClusterSWVersion?: boolean;
  [key: string]: FieldValue;
}

/** The GraphQL `subEquipment` connection a cluster loads its components from. */
export interface SubEquipmentConnection {
  items?: SubEquipmentItem[];
}

/**
 * Shallow camelCase -> snake_case for a single key. Mirrors the frontend
 * converter used to build the cluster-components grid column fields, so the row
 * keys line up with those columns. Intentionally NOT lodash `snakeCase`, which
 * normalises acronyms/numbers differently (e.g. instrumentGTIN ->
 * "instrument_gtin" vs "instrument_g_t_i_n" here).
 */
export const toSnakeCase = (value: string): string =>
  value.replace(/([A-Z])/g, "_$1").trim().toLowerCase();

/** Shallow-converts a row's top-level keys from camelCase to snake_case. */
export const keysToSnakeCase = (row: SubEquipmentItem): FieldValues =>
  Object.entries(row).reduce<FieldValues>((values, [key, value]) => {
    values[toSnakeCase(key)] = value;
    return values;
  }, {});
